import {
	Button,
	Upload,
	Col,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Row,
	Select,
	Space,
	Table,
	Tooltip,
	Typography,
} from "antd";
import {
	CheckOutlined,
	CloseOutlined,
	DeleteOutlined,
	DownloadOutlined,
	EditOutlined,
	InboxOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
	expenseUpdateAction,
	loadExpenses,
	loadExpensesByReporting,
	loadExpenseStatusMaster,
	saveExpense,
} from "../../redux/rtk/features/expense/expenseSlice";
import {
	loadExpenseTypes,
	saveExpenseType,
} from "../../redux/rtk/features/expense/expenseTypeSlice";
import { loadAllProject } from "../../redux/rtk/features/projectManagement/project/project/project";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import getCompanyIdFromToken from "../../utils/getCompanyIdFromToken";
import getUserFromToken from "../../utils/getUserFromToken";

const { Title } = Typography;
const { Dragger } = Upload;
const EXPENSE_TYPE_ID = 1;
const DATE_FORMAT = "YYYY-MM-DD";
const API_BASE_URL = (process.env.REACT_APP_API || "https://hrmsapi.softomation.com")
	.replace(/^["']|["']$/g, "")
	.replace(/\/+$/, "");

const getStatusLabel = (value, options = []) =>
	options.find((option) => option.value === Number(value))?.label ?? "Pending";

const resolveReceiptUrl = (receiptPath) => {
	if (!receiptPath) return "";
	const normalizedPath = String(receiptPath).trim();
	if (!normalizedPath) return "";
	if (normalizedPath.startsWith("data:") || /^https?:\/\//i.test(normalizedPath)) {
		return normalizedPath;
	}
	if (normalizedPath.startsWith("/")) {
		return `${API_BASE_URL}${normalizedPath}`;
	}
	return `${API_BASE_URL}/${normalizedPath}`;
};

// Read file as base64 data URI for ReceiptPath (data:image/png;base64,... etc.)
const getBase64FromFile = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result);
		reader.onerror = (err) => reject(err);
	});

const statusStringToId = (status, statusNameToIdMap = new Map()) => {
	const normalized = (status ?? "").toString().toUpperCase();
	const matchedStatusId = statusNameToIdMap.get(normalized);
	if (matchedStatusId !== undefined) {
		return matchedStatusId;
	}
	if (normalized === "APPROVED") {
		return 1;
	}
	if (normalized === "REJECTED") {
		return 0;
	}
	if (normalized === "PARTIAL APPROVED" || normalized === "PARTIALLY APPROVED") {
		return 2;
	}
	if (normalized === "PENDING") {
		return 3;
	}
	return 0;
};

const AddExpense = ({
	drawer,
	onCancel,
	formOnly,
	tableOnly,
	listSource,
	employeeId: employeeIdProp,
	onSuccess,
}) => {
	const dispatch = useDispatch();
	const employeeIdFromToken = getUserFromToken();
	const companyIdFromToken = getCompanyIdFromToken();
	const [form] = Form.useForm();
	const [expenseTypeForm] = Form.useForm();
	const [editingExpense, setEditingExpense] = useState(null);
	const [receiptFileList, setReceiptFileList] = useState([]);
	const [receiptBase64, setReceiptBase64] = useState(null); // data:image/png;base64,... for API
	const [isExpenseTypeModalVisible, setIsExpenseTypeModalVisible] = useState(false);
	const [expenseTypeSubmitting, setExpenseTypeSubmitting] = useState(false);
	const [pendingExpenseTypeName, setPendingExpenseTypeName] = useState(null);
	const [expenseTypeSearchValue, setExpenseTypeSearchValue] = useState("");

	const { list = [], expensesByReporting = [], loading } = useSelector(
		(state) => state.expense || {}
	);
	const { expenseStatusMaster = [] } = useSelector((state) => state.expense || {});
	const expenseList = listSource === "reporting" ? expensesByReporting : list;
	const { list: projectList = [] } = useSelector((state) => state.project || {});
	const { list: employeeList = [] } = useSelector((state) => state.users || {});
	const { list: expenseTypeList = [] } = useSelector((state) => state.expenseType || {});

	useEffect(() => {
		dispatch(loadAllProject());
		if (listSource === "reporting" && (employeeIdProp ?? employeeIdFromToken)) {
			dispatch(loadExpensesByReporting(employeeIdProp ?? employeeIdFromToken));
		} else {
			// dispatch(loadExpenses());
		}
		dispatch(loadAllStaff({ status: "true" }));
		dispatch(loadExpenseTypes());
		dispatch(loadExpenseStatusMaster());
	}, [dispatch, listSource, employeeIdProp, employeeIdFromToken]);

	const statusOptions = useMemo(() => {
		const mappedOptions = Array.isArray(expenseStatusMaster)
			? expenseStatusMaster
					.map((status) => {
						const value = Number(status.ExpenseStatusId ?? status.statusId ?? status.id);
						const label = status.ExpenseStatusName ?? status.statusName ?? status.name;
						if (!Number.isFinite(value) || label === undefined || label === null) {
							return null;
						}
						return {
							value,
							label: String(label),
						};
					})
					.filter(Boolean)
			: [];

		return mappedOptions;
	}, [expenseStatusMaster]);

	const statusNameToIdMap = useMemo(() => {
		const map = new Map();
		statusOptions.forEach((option) => {
			const normalizedLabel = option.label.toString().trim().toUpperCase();
			if (normalizedLabel) {
				map.set(normalizedLabel, option.value);
			}
		});
		return map;
	}, [statusOptions]);

	const defaultStatusId = useMemo(() => {
		const pendingOption = statusOptions.find(
			(option) => option.label?.toString().trim().toUpperCase() === "PENDING"
		);
		if (pendingOption) {
			return pendingOption.value;
		}
		return statusOptions.length ? statusOptions[0].value : 0;
	}, [statusOptions]);

	const projectOptions = useMemo(
		() =>
			Array.isArray(projectList)
				? projectList
						.map((project) => {
							const value = Number(project.ProjectId ?? project.id ?? project.Id ?? 0);
							const label =
								project.ProjectName ?? project.name ?? (value ? `Project #${value}` : null);
							if (!value || !label) {
								return null;
							}
							return { value, label };
						})
						.filter(Boolean)
				: [],
		[projectList]
	);

	const projectNameMap = useMemo(() => {
		const map = new Map();
		projectOptions.forEach((option) => map.set(option.value, option.label));
		return map;
	}, [projectOptions]);

const expenseTypeOptions = useMemo(
	() =>
		Array.isArray(expenseTypeList)
			? expenseTypeList
					.map((type) => {
						const value = Number(
							type.ExpanseTypeId ??
								type.ExpenseTypeId ??
								type.expenseTypeId ??
								type.id ??
								0
						);
						const label =
							type.ExpanseTypeName ??
							type.ExpenseTypeName ??
							type.expenseTypeName ??
							type.name ??
							null;
						if (!value || !label) {
							return null;
						}
						return { value, label };
					})
					.filter(Boolean)
			: [],
	[expenseTypeList]
);

const expenseTypeMap = useMemo(() => {
	const map = new Map();
	expenseTypeOptions.forEach((option) => map.set(option.value, option.label));
	return map;
}, [expenseTypeOptions]);

const employeeOptions = useMemo(
	() =>
		Array.isArray(employeeList)
			? employeeList
					.map((employee) => {
						const value = Number(employee.EmployeeId ?? employee.id ?? 0);
						const label =
							employee.EmployeeName ??
							[employee.firstName, employee.lastName].filter(Boolean).join(" ").trim();
						if (!value || !label) {
							return null;
						}
						return { value, label };
					})
					.filter(Boolean)
			: [],
	[employeeList]
);

const employeeNameMap = useMemo(() => {
	const map = new Map();
	employeeOptions.forEach((option) => map.set(option.value, option.label));
	return map;
}, [employeeOptions]);

useEffect(() => {
	if (!editingExpense) {
		form.setFieldsValue({
			EmployeeId: Number(employeeIdFromToken) || null,
			StatusId: form.getFieldValue("StatusId") ?? defaultStatusId,
			ExpenseTypeId: form.getFieldValue("ExpenseTypeId"),
		});
	}
}, [defaultStatusId, editingExpense, employeeIdFromToken, form]);

useEffect(() => {
	if (editingExpense) {
		return;
	}
	const currentValue = form.getFieldValue("ExpenseTypeId");
	if (
		(currentValue === undefined || currentValue === null) &&
		expenseTypeOptions.length
	) {
		form.setFieldsValue({ ExpenseTypeId: expenseTypeOptions[0].value });
	}
}, [editingExpense, expenseTypeOptions, form]);

useEffect(() => {
	if (!pendingExpenseTypeName) {
		return;
	}
	const match = expenseTypeOptions.find((option) => {
		const normalizedLabel = option.label ? option.label.toString().trim().toLowerCase() : "";
		return normalizedLabel === pendingExpenseTypeName;
	});
	if (match) {
		form.setFieldsValue({ ExpenseTypeId: match.value });
		setPendingExpenseTypeName(null);
	}
}, [expenseTypeOptions, form, pendingExpenseTypeName]);

	const normalizedExpenses = useMemo(
		() =>
			Array.isArray(expenseList)
				? expenseList.map((item, index) => {
						const expenseId = Number(item.ExpenseId ?? item.expenseId ?? item.Id ?? item.id ?? 0);
						const rawProjectId = item.ProjectId ?? item.projectId ?? null;
						const projectId =
							rawProjectId !== null && rawProjectId !== undefined ? Number(rawProjectId) : null;
						const projectName =
							item.ProjectName ??
							item.projectName ??
							(projectId !== null ? projectNameMap.get(projectId) : "");
						const employeeValue = Number(
							item.EmployeeId ?? item.employeeId ?? employeeIdFromToken ?? 0
						);
						const expenseTypeValue = Number(
							item.ExpanseTypeId ??
								item.ExpenseTypeId ??
								item.expenseTypeId ??
								EXPENSE_TYPE_ID
						);
						const expenseTypeName =
							item.ExpanseTypeName ??
							item.ExpenseTypeName ??
							item.expenseTypeName ??
							expenseTypeMap.get(expenseTypeValue) ??
							"";
						const employeeName =
							item.EmployeeName ??
							item.employeeName ??
							employeeNameMap.get(employeeValue) ??
							"";
						const rawStatus = item.StatusId ?? item.statusId ?? item.Status ?? item.status ?? 0;
						const statusId =
							typeof rawStatus === "string"
								? statusStringToId(rawStatus, statusNameToIdMap)
								: Number(rawStatus ?? 0);
						return {
							key: expenseId || index,
							ExpenseId: expenseId,
							EmployeeId: employeeValue,
							EmployeeName: employeeName,
							ProjectId: projectId,
							ProjectName: projectName,
							ExpenseTypeId: expenseTypeValue,
							ExpenseTypeName: expenseTypeName,
							ExpenseDate: item.ExpenseDate ?? item.expenseDate ?? null,
							Amount: Number(item.Amount ?? item.amount ?? 0),
							ApprovedAmount:
								item.ApprovedAmount !== undefined && item.ApprovedAmount !== null
									? Number(item.ApprovedAmount ?? item.approvedAmount)
									: null,
							Remarks: item.Remarks ?? item.remarks ?? "",
							ReceiptPath:
								item.ReceiptPath ??
								item.receiptPath ??
								item.ReceiptFullPath ??
								item.receiptFullPath ??
								"",
							ReceiptFullPath: resolveReceiptUrl(
								item.ReceiptFullPath ??
									item.receiptFullPath ??
									item.ReceiptPath ??
									item.receiptPath ??
									""
							),
							StatusId: statusId,
							ExpenseStatusName: item.ExpenseStatusName ?? item.expenseStatusName ?? null,
							DataStatus: item.DataStatus ?? item.dataStatus ?? 1,
						};
				  })
				: [],
		[
			employeeNameMap,
			employeeIdFromToken,
			expenseList,
			expenseTypeMap,
			projectNameMap,
			statusNameToIdMap,
		]
	);

	const handleResetForm = useCallback(() => {
		setEditingExpense(null);
		form.resetFields();
		form.setFieldsValue({
			EmployeeId: Number(employeeIdFromToken) || null,
			StatusId: defaultStatusId,
			ExpenseTypeId: expenseTypeOptions.length ? expenseTypeOptions[0].value : undefined,
		});
		setReceiptFileList([]);
		setReceiptBase64(null);
	}, [defaultStatusId, employeeIdFromToken, expenseTypeOptions, form]);

const openExpenseTypeModal = useCallback(
	(initialName = "") => {
		setPendingExpenseTypeName(null);
		const trimmed = (initialName ?? "").trim();
		expenseTypeForm.resetFields();
		expenseTypeForm.setFieldsValue({
			ExpanseTypeName: trimmed,
			ExpanseTypeDescription: "",
		});
		setIsExpenseTypeModalVisible(true);
	},
	[expenseTypeForm]
);

const handleExpenseTypeModalCancel = useCallback(() => {
	setIsExpenseTypeModalVisible(false);
	setPendingExpenseTypeName(null);
	expenseTypeForm.resetFields();
	setExpenseTypeSearchValue("");
}, [expenseTypeForm]);

const handleExpenseTypeSubmit = useCallback(async () => {
	try {
		const values = await expenseTypeForm.validateFields();
		const name = (values.ExpanseTypeName ?? "").trim();
		const description = (values.ExpanseTypeDescription ?? "").trim() || null;
		if (!name) {
			return;
		}
		setExpenseTypeSubmitting(true);
		setPendingExpenseTypeName(name.toLowerCase());
		const response = await dispatch(
			saveExpenseType({
				ExpanseTypeId: 0,
				ExpanseTypeName: name,
				ExpanseTypeDescription: description,
				DataStatus: 1,
				CompanyId: companyIdFromToken ? Number(companyIdFromToken) : null,
			})
		);
		if (response?.meta?.requestStatus !== "rejected") {
			setIsExpenseTypeModalVisible(false);
			expenseTypeForm.resetFields();
			setExpenseTypeSearchValue("");
			dispatch(loadExpenseTypes());
		} else {
			setPendingExpenseTypeName(null);
		}
	} finally {
		setExpenseTypeSubmitting(false);
	}
}, [companyIdFromToken, dispatch, expenseTypeForm]);

	const handleEditExpense = useCallback(
		(record) => {
			setEditingExpense(record);
			form.setFieldsValue({
				EmployeeId: record.EmployeeId ?? Number(employeeIdFromToken) ?? null,
				ProjectId: record.ProjectId ?? null,
				Amount: record.Amount ?? null,
				ApprovedAmount: record.ApprovedAmount ?? null,
				ExpenseTypeId: record.ExpenseTypeId ?? null,
				ExpenseDate: record.ExpenseDate ? dayjs(record.ExpenseDate) : null,
				Remarks: record.Remarks ?? "",
				StatusId: record.StatusId ?? 0,
			});
			setReceiptFileList(
				record.ReceiptPath && !String(record.ReceiptPath).startsWith("data:")
					? [
							{
								uid: `${record.ExpenseId ?? Date.now()}`,
								name: record.ReceiptPath,
								status: "done",
							},
						]
					: []
			);
			setReceiptBase64(null); // new edit: use existing ReceiptPath from server until user picks new file
		},
		[employeeIdFromToken, form]
	);

	const handleDeleteExpense = useCallback(
		async (record) => {
			const payload = {
				ExpenseId: record.ExpenseId,
				EmployeeId: record.EmployeeId || Number(employeeIdFromToken) || 0,
				ProjectId: record.ProjectId ?? null,
				ExpenseTypeId:
					record.ExpenseTypeId ?? form.getFieldValue("ExpenseTypeId") ?? EXPENSE_TYPE_ID,
				ExpenseDate: record.ExpenseDate ? dayjs(record.ExpenseDate).format(DATE_FORMAT) : null,
				Amount: record.Amount ?? 0,
				ApprovedAmount: record.ApprovedAmount ?? null,
				Remarks: record.Remarks ?? null,
				ReceiptPath: record.ReceiptPath ?? null,
				StatusId: record.StatusId ?? 0,
				DataStatus: 2,
			};
			const response = await dispatch(saveExpense(payload));
			if (response?.meta?.requestStatus !== "rejected") {
				if (listSource === "reporting" && (employeeIdProp ?? employeeIdFromToken)) {
					dispatch(loadExpensesByReporting(employeeIdProp ?? employeeIdFromToken));
				} else {
					// dispatch(loadExpenses());
				}
				if (editingExpense?.ExpenseId === record.ExpenseId) {
					handleResetForm();
				}
			}
		},
		[
			dispatch,
			editingExpense,
			employeeIdFromToken,
			form,
			handleResetForm,
			listSource,
			employeeIdProp,
		]
	);

	const onFinish = async (values) => {
		// ReceiptPath: new upload as base64 data URI (data:image/png;base64,...); else keep existing
		let receiptPath = receiptBase64 ?? editingExpense?.ReceiptPath ?? null;

		const selectedEmployeeId =
			values.EmployeeId !== undefined && values.EmployeeId !== null
				? Number(values.EmployeeId)
				: Number(employeeIdFromToken);

		// Add: ExpenseId=0, Edit: selected ExpenseId, DataStatus=1 (delete uses DataStatus=2 in handleDeleteExpense)
		const payload = {
			ExpenseId: editingExpense?.ExpenseId ?? 0,
			EmployeeId: selectedEmployeeId,
			ProjectId: values.ProjectId ?? null,
			ExpenseTypeId:
				values.ExpenseTypeId !== undefined && values.ExpenseTypeId !== null
					? Number(values.ExpenseTypeId)
					: expenseTypeOptions.length
						? expenseTypeOptions[0].value
						: EXPENSE_TYPE_ID,
			ExpenseDate: values.ExpenseDate ? dayjs(values.ExpenseDate).format(DATE_FORMAT) : null,
			Amount:
				values.Amount !== undefined && values.Amount !== null ? Number(values.Amount) : 0,
			ApprovedAmount:
				values.ApprovedAmount !== undefined && values.ApprovedAmount !== null
					? Number(values.ApprovedAmount)
					: null,
			Remarks: values.Remarks ?? null,
			ReceiptPath: receiptPath,
			StatusId: values.StatusId ?? defaultStatusId,
			DataStatus: editingExpense?.DataStatus ?? 1,
		};

		const response = await dispatch(saveExpense(payload));
		if (response?.meta?.requestStatus !== "rejected") {
			if (listSource === "reporting" && (employeeIdProp ?? employeeIdFromToken)) {
				dispatch(loadExpensesByReporting(employeeIdProp ?? employeeIdFromToken));
			} else {
				// dispatch(loadExpenses());
			}
			onSuccess?.();
			handleResetForm();
			onCancel?.();
		}
	};

	const onFinishFailed = () => {
		toast.warning("Please review the form for errors before submitting");
	};

	const handleViewReceipt = useCallback((receiptPath) => {
		if (!receiptPath) return;
		const previewUrl = resolveReceiptUrl(receiptPath);
		if (previewUrl) {
			window.open(previewUrl, "_blank", "noopener,noreferrer");
			return;
		}
		toast.info(String(receiptPath));
	}, []);

	const handleDownloadReceipt = useCallback(async (receiptPath) => {
		if (!receiptPath) return;
		const fileUrl = resolveReceiptUrl(receiptPath);
		if (!fileUrl) {
			toast.warning("Receipt file is not available for download");
			return;
		}
		try {
			const accessToken = localStorage.getItem("access-token");
			const response = await fetch(fileUrl, {
				method: "GET",
				headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
			});
			if (!response.ok) {
				throw new Error("Failed to download file");
			}

			const blob = await response.blob();
			const objectUrl = window.URL.createObjectURL(blob);
			const disposition = response.headers.get("content-disposition") || "";
			const dispositionMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^";\n]+)/i);
			const headerFileName = dispositionMatch?.[1]
				? decodeURIComponent(dispositionMatch[1].replace(/"/g, "").trim())
				: "";
			const pathFileName =
				typeof receiptPath === "string" && receiptPath.trim()
					? receiptPath.split("/").pop()
					: "";
			const downloadFileName = headerFileName || pathFileName || "expense-receipt";

			const anchor = document.createElement("a");
			anchor.href = objectUrl;
			anchor.download = downloadFileName;
			document.body.appendChild(anchor);
			anchor.click();
			document.body.removeChild(anchor);
			window.URL.revokeObjectURL(objectUrl);
		} catch (_error) {
			toast.error("Unable to download receipt. Please try again.");
		}
	}, []);

	const refetchList = useCallback(() => {
		if (listSource === "reporting" && (employeeIdProp ?? employeeIdFromToken)) {
			dispatch(loadExpensesByReporting(employeeIdProp ?? employeeIdFromToken));
		} else {
			// dispatch(loadExpenses());
		}
	}, [dispatch, listSource, employeeIdProp, employeeIdFromToken]);

	const handleAcceptExpense = useCallback(
		async (record) => {
			const expenseId = record.ExpenseId ?? record.expenseId ?? record.id;
			if (!expenseId) return;
		const approvedStatusId = statusNameToIdMap.get("APPROVED") ?? 1;
		const result = await dispatch(
			expenseUpdateAction({ ExpenseId: expenseId, StatusId: approvedStatusId })
		);
			if (result?.meta?.requestStatus === "fulfilled") refetchList();
		},
		[dispatch, refetchList, statusNameToIdMap]
	);

	const handleRejectExpense = useCallback(
		async (record) => {
			const expenseId = record.ExpenseId ?? record.expenseId ?? record.id;
			if (!expenseId) return;
		const rejectedStatusId = statusNameToIdMap.get("REJECTED") ?? 0;
		const result = await dispatch(
			expenseUpdateAction({ ExpenseId: expenseId, StatusId: rejectedStatusId })
		);
			if (result?.meta?.requestStatus === "fulfilled") refetchList();
		},
		[dispatch, refetchList, statusNameToIdMap]
	);

	const columns = useMemo(
		() => [
			{
				title: "Date",
				dataIndex: "ExpenseDate",
				key: "ExpenseDate",
				render: (value) => (value ? dayjs(value).format(DATE_FORMAT) : "-"),
			},
			{
				title: "Employee",
				dataIndex: "EmployeeName",
				key: "EmployeeName",
				render: (_, record) =>
					record.EmployeeName || employeeNameMap.get(record.EmployeeId) || "-",
			},
			{
				title: "Project",
				dataIndex: "ProjectName",
				key: "ProjectName",
				render: (_, record) =>
					record.ProjectName || projectNameMap.get(record.ProjectId) || "-",
			},
			{
				title: "Expense Type",
				dataIndex: "ExpenseTypeName",
				key: "ExpenseTypeName",
				render: (_, record) =>
					record.ExpenseTypeName ||
					expenseTypeMap.get(record.ExpenseTypeId) ||
					"-",
			},
			{
				title: "Amount",
				dataIndex: "Amount",
				key: "Amount",
				render: (value) =>
					value !== undefined && value !== null ? Number(value).toFixed(2) : "0.00",
			},
			{
				title: "Approved Amount",
				dataIndex: "ApprovedAmount",
				key: "ApprovedAmount",
				render: (value) =>
					value !== undefined && value !== null ? Number(value).toFixed(2) : "-",
			},
			{
				title: "Status",
				dataIndex: "StatusId",
				key: "StatusId",
				render: (value, record) =>
					record.ExpenseStatusName ?? getStatusLabel(value, statusOptions),
			},
			{
				title: "Remarks",
				dataIndex: "Remarks",
				key: "Remarks",
				render: (value) => value || "-",
			},
			{
				title: "Receipt",
				dataIndex: "ReceiptPath",
				key: "ReceiptPath",
				render: (value, record) =>
					(record?.ReceiptFullPath || value) ? (
						<Space size={4}>
							<Button
								type="link"
								onClick={() => handleViewReceipt(record?.ReceiptFullPath || value)}
							>
								View
							</Button>
							<Button
								type="text"
								icon={<DownloadOutlined />}
								onClick={() => handleDownloadReceipt(record?.ReceiptFullPath || value)}
							/>
						</Space>
					) : (
						"-"
					),
			},
			{
				title: "Actions",
				key: "actions",
				render: (_, record) => (
					<Space>
						<Tooltip title="Accept">
							<Button
								type="text"
								icon={<CheckOutlined style={{ color: "#16a34a" }} />}
								onClick={() => handleAcceptExpense(record)}
							/>
						</Tooltip>
						<Tooltip title="Reject">
							<Button
								type="text"
								icon={<CloseOutlined style={{ color: "#dc2626" }} />}
								onClick={() => handleRejectExpense(record)}
							/>
						</Tooltip>
						<Tooltip title="Edit">
							<Button type="text" icon={<EditOutlined />} onClick={() => handleEditExpense(record)} />
						</Tooltip>
						<Popconfirm
							title="Delete expense?"
							onConfirm={() => handleDeleteExpense(record)}
							okText="Yes"
							cancelText="No"
						>
							<Tooltip title="Delete">
								<Button type="text" danger icon={<DeleteOutlined />} />
							</Tooltip>
						</Popconfirm>
					</Space>
				),
			},
		],
		[
			employeeNameMap,
			expenseTypeMap,
			handleAcceptExpense,
			handleDeleteExpense,
			handleEditExpense,
			handleDownloadReceipt,
			handleRejectExpense,
			handleViewReceipt,
			projectNameMap,
			statusOptions,
		]
	);

    return (
      <div className="add-expense-container">
			{/* <UserPrivateComponent permission={"create-leaveApplication"}> */}
          <Row className="expense-row" gutter={[16, 16]}>
            {!tableOnly && (
            <Col
              xs={24}
              sm={24}
					md={formOnly ? 24 : 10}
					lg={formOnly ? 24 : 10}
					xl={formOnly ? 24 : 9}
              className="column-design border rounded card-custom expense-form-col"
            >
              <Title level={4} className="m-2 mt-2 mb-5 text-center">
							{editingExpense ? "Update Expense" : "Add Expense"}
              </Title>
              <Form
                form={form}
                style={{ marginBottom: "40px" }}
							name="expense-form"
							labelCol={{ span: 7 }}
							wrapperCol={{ span: 12 }}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
              >
                <Form.Item
                style={{ marginBottom: "10px" }}
								label="Employee"
								name="EmployeeId"
                rules={[
                  {
                    required: true,
										message: "Please select an employee",
                  },
                ]}
              >
								<Select
									placeholder="Select an employee"
									options={employeeOptions}
									allowClear
									showSearch
									optionFilterProp="label"
								/>
              </Form.Item>

                <Form.Item
                style={{ marginBottom: "10px" }}
                label="Select Project"
								name="ProjectId"
                rules={[
                  {
                    required: true,
                    message: "Please select a project",
                  },
                ]}
              >
								<Select
									placeholder="Select a project"
									options={projectOptions}
									allowClear
									showSearch
									optionFilterProp="label"
								/>
              </Form.Item>

                <Form.Item
                  style={{ marginBottom: "10px" }}
					label="Expense Type"
					name="ExpenseTypeId"
                  rules={[
                    {
                      required: true,
							message: "Please select an expense type",
                    },
                  ]}
                >
					<Select
						placeholder="Select an expense type"
						showSearch
						allowClear
						options={expenseTypeOptions}
						optionFilterProp="label"
						onSearch={(value) => setExpenseTypeSearchValue(value)}
						onClear={() => setExpenseTypeSearchValue("")}
						onChange={() => setExpenseTypeSearchValue("")}
						onDropdownVisibleChange={(open) => {
							if (!open) {
								setExpenseTypeSearchValue("");
							}
						}}
						dropdownRender={(menu) => (
							<>
								{menu}
								<div style={{ padding: 8 }}>
									<Button
										type="link"
										icon={<PlusOutlined />}
										onMouseDown={(event) => event.preventDefault()}
										onClick={() => openExpenseTypeModal(expenseTypeSearchValue)}
										block
									>
										Add Expense Type
									</Button>
								</div>
							</>
						)}
						notFoundContent={
							expenseTypeSearchValue?.trim() ? (
								<div style={{ padding: 8 }}>
									<Button
										type="link"
										icon={<PlusOutlined />}
										onMouseDown={(event) => event.preventDefault()}
										onClick={() => openExpenseTypeModal(expenseTypeSearchValue)}
										block
									>
										Add "{expenseTypeSearchValue.trim()}"
									</Button>
								</div>
							) : null
						}
					/>
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: "10px" }}
                  label="Amount"
								name="Amount"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the expense amount",
                    },
								]}
							>
								<InputNumber
									style={{ width: "100%" }}
									min={0}
									step={0.01}
									placeholder="Enter amount"
								/>
							</Form.Item>

							<Form.Item
								style={{ marginBottom: "10px" }}
								label="Approved Amount"
								name="ApprovedAmount"
							>
								<InputNumber
									style={{ width: "100%" }}
									min={0}
									step={0.01}
									placeholder="Enter approved amount (optional)"
								/>
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: "20px" }}
                  label="Date"
								name="ExpenseDate"
                  rules={[
                    {
                      required: true,
                      message: "Please select the expense date",
                    },
                  ]}
                >
								<DatePicker style={{ width: "100%" }} format={DATE_FORMAT} />
							</Form.Item>

							<Form.Item
								style={{ marginBottom: "10px" }}
								label="Status"
								name="StatusId"
								initialValue={defaultStatusId}
							>
								<Select options={statusOptions} />
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: "10px" }}
								label="Receipt"
                >
                  <Dragger
									name="receipt"
									fileList={receiptFileList}
                    beforeUpload={() => false}
									onChange={({ fileList }) => {
										const nextList = fileList.slice(-1);
										setReceiptFileList(nextList);
										if (nextList.length === 0) {
											setReceiptBase64(null);
											return;
										}
										const file = nextList[0]?.originFileObj ?? nextList[0];
										if (file && file instanceof File) {
											getBase64FromFile(file)
												.then((dataUri) => setReceiptBase64(dataUri))
												.catch(() => {
													setReceiptBase64(null);
													toast.error("Failed to read receipt file");
												});
										}
									}}
                    maxCount={1}
									accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,image/jpg,application/pdf"
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
									<p className="ant-upload-text">
										Click or drag file to upload receipt (optional)
									</p>
                    <p className="ant-upload-hint">
										JPG, PNG or PDF. Sent as base64 (data:image/xxx;base64,...).
                    </p>
                  </Dragger>
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: "10px" }}
								label="Remarks"
								name="Remarks"
							>
								<Input.TextArea rows={3} placeholder="Enter remarks (optional)" />
							</Form.Item>

							<Form.Item wrapperCol={{ offset: 7, span: 12 }}>
								<Space>
									<Button type="primary" size="large" htmlType="submit" loading={loading}>
										{editingExpense ? "Update Expense" : "Add Expense"}
									</Button>
									{editingExpense ? (
										<Button onClick={handleResetForm} disabled={loading}>
											Cancel
                  </Button>
									) : null}
								</Space>
                </Form.Item>
              </Form>
				</Col>
            )}
				{(tableOnly || !formOnly) && (
				<Col xs={24} sm={24} md={tableOnly ? 24 : 14} lg={tableOnly ? 24 : 14} xl={tableOnly ? 24 : 15} className="expense-table-col">
					<div className="expense-table-wrapper">
			<Table
				dataSource={normalizedExpenses}
              columns={columns}
				loading={loading}
				rowKey="key"
				pagination={{ pageSize: 10 }}
				className="column-design border rounded card-custom"
							scroll={{ x: true, y: "100%" }}
			/>
					</div>
            </Col>
				)}
          </Row>
			{/* </UserPrivateComponent> */}
			<Modal
				title="Add Expense Type"
				open={isExpenseTypeModalVisible}
				onCancel={handleExpenseTypeModalCancel}
				onOk={handleExpenseTypeSubmit}
				okText="Save"
				cancelText="Cancel"
				confirmLoading={expenseTypeSubmitting}
				destroyOnClose
			>
				<Form
					form={expenseTypeForm}
					layout="vertical"
					autoComplete="off"
				>
					<Form.Item
						label="Expense Type Name"
						name="ExpanseTypeName"
						rules={[{ required: true, message: "Please enter expense type name" }]}
					>
						<Input placeholder="Enter expense type name" />
					</Form.Item>
					<Form.Item
						label="Description"
						name="ExpanseTypeDescription"
					>
						<Input.TextArea rows={3} placeholder="Enter description (optional)" />
					</Form.Item>
				</Form>
			</Modal>
      </div>
    );
  };

  export default AddExpense; 